import { useState, useEffect } from "react";
import { Search, Loader2, FileCheck, HandCoins, Landmark, Sparkles } from "lucide-react";
import {
  fetchFacturesAchatByTransactionId,
  searchFacturesAchatForTransaction,
  rapprocherFactureAchat,
  detacherRapprochementTransaction,
  partitionFacturesSuggerees,
  updateTransactionTresorerieDirecte,
  errorMessageFromUnknown,
  TRESORERIE_PRESETS_SORTIE,
  tresoreriePresetFor,
  type FactureAchatRow,
  type FactureAchatRapprochee,
  type ModeTraitement,
  type TypeTresorerieDirecte,
} from "../../../lib/transactions";
import {
  searchFacturesVenteForTransaction,
  searchAvoirsVenteForTransactionSortie,
  rapprocherFacturesVenteMulti,
  detacherFactureVente,
  type FactureVenteRow,
  type FactureVenteRapprochee,
  fetchFacturesVenteByTransactionId,
} from "../../../lib/facturation";
import { formatMontantFCFA } from "@/lib/formatMontant";
import {
  createRegle,
  extraireMotClePourRegle,
  fetchRegles,
  findMatchingRegleTransaction,
  type RegleCategorisation,
} from "../../../lib/regles";

const fmt = (n: number) => formatMontantFCFA(n);

/** Alignement léger sur 8 chiffres pour rapprocher compte règle / préréglages. */
function normaliserCompte8(raw: string): string {
  const digits = (raw ?? "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  return digits.length <= 8 ? digits.padEnd(8, "0") : digits.slice(0, 8);
}

function typeTresorerieSortieDepuisCompte(compte: string): TypeTresorerieDirecte {
  const n = normaliserCompte8(compte);
  if (!n) return "autre";
  for (const key of Object.keys(TRESORERIE_PRESETS_SORTIE) as (keyof typeof TRESORERIE_PRESETS_SORTIE)[]) {
    if (normaliserCompte8(TRESORERIE_PRESETS_SORTIE[key].compte) === n) {
      return key;
    }
  }
  return "autre";
}

export interface TransactionReconciliationTabProps {
  transactionId: string;
  txType: "entree" | "sortie";
  montantTTC: number;
  date: string;
  libelle: string;
  suggestedTier?: string | null;
  modeTraitement?: ModeTraitement | null;
  dossierId: string;
  onRapprochement?: () => void;
}

export default function TransactionReconciliationTab({
  transactionId,
  txType,
  montantTTC,
  date,
  libelle,
  suggestedTier,
  modeTraitement,
  dossierId,
  onRapprochement,
}: TransactionReconciliationTabProps) {
  const [mode, setMode] = useState<ModeTraitement | "remboursement_client">(modeTraitement ?? "facture");
  const [linkedAchat, setLinkedAchat] = useState<FactureAchatRapprochee[]>([]);
  const [linkedVente, setLinkedVente] = useState<FactureVenteRapprochee[]>([]);
  const fournisseurNom = suggestedTier ?? "";
  const [suggestedFactureAchat, setSuggestedFactureAchat] = useState<FactureAchatRow[] | null>(null);
  const [searchFactureVente, setSearchFactureVente] = useState<FactureVenteRow[] | null>(null);
  const [selectionVente, setSelectionVente] = useState<Array<{ facture: FactureVenteRow; montant: number }>>([]);
  const [loadingAchat, setLoadingAchat] = useState(false);
  const [loadingVente, setLoadingVente] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tresorerieForm, setTresorerieForm] = useState({ type: "autre" as TypeTresorerieDirecte, compte: "", libelle: "" });
  const [regles, setRegles] = useState<RegleCategorisation[]>([]);
  const [regleMatch, setRegleMatch] = useState<RegleCategorisation | null>(null);
  const [propositionRegleTreso, setPropositionRegleTreso] = useState<{
    motCle: string;
    compteNumero: string;
    tauxTva: number | null;
  } | null>(null);

  useEffect(() => {
    void fetchRegles(dossierId)
      .then(setRegles)
      .catch(() => setRegles([]));
  }, [dossierId]);

  useEffect(() => {
    const match = findMatchingRegleTransaction({ libelle, montant_ttc: montantTTC, type: txType }, regles);
    setRegleMatch(match);

    if (match?.compte_charge_numero?.trim()) {
      const compte = match.compte_charge_numero.trim();
      const typeInfere = typeTresorerieSortieDepuisCompte(compte);
      setTresorerieForm((prev) => ({
        ...prev,
        compte,
        type: typeInfere,
      }));
    }
  }, [transactionId, libelle, montantTTC, txType, regles]);

  useEffect(() => {
    if (txType === "sortie") {
      fetchFacturesAchatByTransactionId(transactionId).then(setLinkedAchat).catch(() => setLinkedAchat([]));
    }
  }, [transactionId, txType]);

  useEffect(() => {
    setPropositionRegleTreso(null);
  }, [transactionId]);

  useEffect(() => {
    if (txType === "entree" || txType === "sortie") {
      fetchFacturesVenteByTransactionId(transactionId).then(setLinkedVente).catch(() => setLinkedVente([]));
    }
  }, [transactionId, txType]);

  const montantRestantTxPourAchat =
    txType === "sortie" ? Math.max(0, Math.abs(montantTTC) - linkedAchat.reduce((s, r) => s + r.montant, 0)) : 0;

  useEffect(() => {
    if (txType !== "sortie" || mode !== "facture") {
      setSuggestedFactureAchat(null);
      return;
    }
    if (montantRestantTxPourAchat < 0.01) {
      setSuggestedFactureAchat(null);
      return;
    }
    let cancelled = false;
    setLoadingAchat(true);
    searchFacturesAchatForTransaction(dossierId, montantTTC, suggestedTier ?? libelle, { transactionId })
      .then((list) => {
        if (!cancelled) setSuggestedFactureAchat(list);
      })
      .catch(() => {
        if (!cancelled) setSuggestedFactureAchat(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAchat(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transactionId, txType, mode, dossierId, montantTTC, suggestedTier, libelle, linkedAchat]);

  const montantRestantEncaissement = txType === "entree"
    ? Math.max(0, montantTTC - linkedVente.reduce((s, r) => s + (r.isAvoir ? -r.montant : r.montant), 0))
    : 0;
  const montantRestantRemboursement = txType === "sortie"
    ? Math.max(0, montantTTC - linkedVente.reduce((s, r) => s + r.montant, 0))
    : 0;
  const sommeSelection = mode === "remboursement_client"
    ? selectionVente.reduce((s, r) => s + r.montant, 0)
    : selectionVente.reduce((s, r) => s + (r.facture.isAvoir ? -r.montant : r.montant), 0);
  const netOk = Math.abs(sommeSelection - montantTTC) < 2;

  const handleRechercherVente = async () => {
    setLoadingVente(true);
    setSearchFactureVente(null);
    setSelectionVente([]);
    try {
      if (txType === "sortie" && mode === "remboursement_client") {
        const list = await searchAvoirsVenteForTransactionSortie(dossierId, montantTTC, transactionId);
        setSearchFactureVente(list);
      } else if (txType === "entree") {
        const list = await searchFacturesVenteForTransaction(dossierId, montantTTC, transactionId, libelle, suggestedTier ?? undefined);
        setSearchFactureVente(list);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoadingVente(false);
    }
  };

  const handleAjouterVente = (f: FactureVenteRow) => {
    if (selectionVente.some((s) => s.facture.id === f.id)) return;
    const def = mode === "remboursement_client"
      ? Math.min(Math.abs(f.restant_du_ttc || 0), Math.max(0.01, montantRestantRemboursement - sommeSelection))
      : Math.min(f.restant_du_ttc || f.montant_ttc, Math.max(0.01, montantRestantEncaissement - sommeSelection));
    setSelectionVente((p) => [...p, { facture: f, montant: def }]);
  };

  const handleValiderVente = async () => {
    if (!netOk || selectionVente.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await rapprocherFacturesVenteMulti(transactionId, selectionVente.map((s) => ({ factureId: s.facture.id, montant: s.montant })), date);
      setLinkedVente(await fetchFacturesVenteByTransactionId(transactionId));
      setSelectionVente([]);
      setSearchFactureVente(null);
      onRapprochement?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRapprocherAchat = async (fa: FactureAchatRow) => {
    setSubmitting(true);
    setError(null);
    try {
      const sommeDeja = linkedAchat.reduce((s, r) => s + r.montant, 0);
      const restTx = Math.max(0.01, Math.round(Math.abs(montantTTC) - sommeDeja));
      const restFa = Math.round(
        fa.restant_du_ttc != null && !Number.isNaN(Number(fa.restant_du_ttc))
          ? Number(fa.restant_du_ttc)
          : Number(fa.montant_ttc) || 0
      );
      const m = Math.min(restTx, Math.max(0.01, restFa));
      await rapprocherFactureAchat(fa.id, transactionId, m);
      setLinkedAchat(await fetchFacturesAchatByTransactionId(transactionId));
      onRapprochement?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDetacherAchat = async (factureId: string) => {
    try {
      await detacherRapprochementTransaction(factureId, transactionId);
      setLinkedAchat(await fetchFacturesAchatByTransactionId(transactionId));
      onRapprochement?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    }
  };

  const handleDetacherVente = async (factureId: string) => {
    try {
      await detacherFactureVente(factureId, transactionId);
      setLinkedVente(await fetchFacturesVenteByTransactionId(transactionId));
      onRapprochement?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    }
  };

  const handleTresorerieDirecte = async () => {
    const p = tresoreriePresetFor("sortie", tresorerieForm.type) ?? TRESORERIE_PRESETS_SORTIE.autre;
    const compte = tresorerieForm.compte.trim() || p.compte;
    const lib = tresorerieForm.libelle.trim() || p.libelle;
    if (!compte || !lib) {
      setError("Compte et libellé requis");
      return;
    }
    const avaitRegleAuto = Boolean(regleMatch?.compte_charge_numero?.trim());
    setSubmitting(true);
    setError(null);
    try {
      await updateTransactionTresorerieDirecte(transactionId, {
        mode_traitement: "tresorerie_directe",
        type_tresorerie_directe: tresorerieForm.type,
        compte_comptable_treso: compte,
        libelle_comptable_treso: lib,
      });
      onRapprochement?.();
      if (!avaitRegleAuto) {
        const motCle = extraireMotClePourRegle(libelle);
        if (motCle) {
          setPropositionRegleTreso({
            motCle,
            compteNumero: compte.trim(),
            tauxTva: null,
          });
        }
      }
    } catch (e) {
      setError(errorMessageFromUnknown(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreerRegleTreso = async () => {
    if (!propositionRegleTreso) return;
    setSubmitting(true);
    setError(null);
    try {
      await createRegle(dossierId, {
        nom: `Règle ${propositionRegleTreso.motCle}`,
        field: "libelle",
        operator: "contains",
        value: propositionRegleTreso.motCle,
        compte_charge_numero: propositionRegleTreso.compteNumero,
        taux_tva: propositionRegleTreso.tauxTva,
      });
      setPropositionRegleTreso(null);
      const next = await fetchRegles(dossierId);
      setRegles(next);
    } catch (e) {
      setError(errorMessageFromUnknown(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {error && (
        <div style={{ padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {txType === "sortie" && (
        <div>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", marginBottom: 8 }}>Mode</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {(["facture", "remboursement_client", "tresorerie_directe"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "none",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: mode === m ? "#F97316" : "#f1f5f9",
                  color: mode === m ? "white" : "#64748b",
                }}
              >
                {m === "facture" && <><FileCheck size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Facture</>}
                {m === "remboursement_client" && <><HandCoins size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Remb. client</>}
                {m === "tresorerie_directe" && <><Landmark size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Trésorerie</>}
              </button>
            ))}
          </div>
        </div>
      )}

      {txType === "sortie" && mode === "facture" && (
        <>
          {linkedAchat.length > 0 && (
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "#059669", marginBottom: 8 }}>Facture(s) rapprochée(s)</div>
              {linkedAchat.map((r) => (
                <div key={r.facture.id} style={{ padding: 12, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#047857" }}>{r.facture.fournisseur_nom} · N° {r.facture.numero}</div>
                    <div style={{ fontSize: 12, color: "#059669" }}>{fmt(r.montant)}</div>
                  </div>
                  <button type="button" onClick={() => handleDetacherAchat(r.facture.id)} style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer" }}>Détacher</button>
                </div>
              ))}
            </div>
          )}
          {montantRestantTxPourAchat >= 0.01 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b" }}>
                  {linkedAchat.length > 0 ? "Autres factures (reste transaction)" : "Factures suggérées"}
                </div>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>Reste {fmt(montantRestantTxPourAchat)}</span>
              </div>
              {loadingAchat ? (
                <div style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}><Loader2 size={18} className="animate-spin" /></div>
              ) : suggestedFactureAchat && suggestedFactureAchat.length > 0 ? (
                (() => {
                  const { suggested, others } = partitionFacturesSuggerees(suggestedFactureAchat, fournisseurNom);
                  const list = suggested ? [suggested, ...others] : others;
                  return list.slice(0, 8).map((fa) => {
                    const restFa =
                      fa.restant_du_ttc != null && !Number.isNaN(Number(fa.restant_du_ttc))
                        ? Number(fa.restant_du_ttc)
                        : Number(fa.montant_ttc) || 0;
                    return (
                      <div key={fa.id} style={{ padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{fa.fournisseur_nom} · N° {fa.numero}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>TTC {fmt(fa.montant_ttc)} · Restant {fmt(restFa)}</div>
                        <button type="button" disabled={submitting} onClick={() => handleRapprocherAchat(fa)} style={{ marginTop: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "white", background: "#059669", border: "none", borderRadius: 6, cursor: submitting ? "not-allowed" : "pointer" }}>Rapprocher</button>
                      </div>
                    );
                  });
                })()
              ) : (
                <div style={{ fontSize: 12, color: "#94a3b8", padding: 8 }}>Aucune facture correspondante (montant / fournisseur).</div>
              )}
            </div>
          )}
        </>
      )}

      {txType === "sortie" && mode === "remboursement_client" && (
        <>
          {linkedVente.length > 0 && (
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "#059669", marginBottom: 8 }}>Avoir(s) rapproché(s)</div>
              {linkedVente.map((r) => (
                <div key={r.facture.id} style={{ padding: 12, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><div style={{ fontWeight: 600 }}>Avoir {r.facture.numero}</div><div style={{ fontSize: 12 }}>{fmt(r.montant)}</div></div>
                  <button type="button" onClick={() => handleDetacherVente(r.facture.id)} style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer" }}>Détacher</button>
                </div>
              ))}
            </div>
          )}
          {(linkedVente.length === 0 || montantRestantRemboursement > 0) && (
            <>
              <button type="button" onClick={handleRechercherVente} disabled={loadingVente} style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#059669", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, cursor: loadingVente ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                {loadingVente ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Rechercher des avoirs
              </button>
              {searchFactureVente?.map((f) => (
                <button key={f.id} type="button" onClick={() => handleAjouterVente(f)} style={{ padding: 10, textAlign: "left", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", width: "100%" }}>
                  Avoir {f.numero} · restant {fmt(Math.abs(f.restant_du_ttc))}
                </button>
              ))}
              {selectionVente.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>Sélection · Total {fmt(sommeSelection)}</div>
                  {selectionVente.map((s) => (
                    <div key={s.facture.id} style={{ padding: 10, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                      <span>Avoir {s.facture.numero} · {fmt(s.montant)}</span>
                      <button type="button" onClick={() => setSelectionVente((p) => p.filter((x) => x.facture.id !== s.facture.id))} style={{ fontSize: 12, color: "#64748b" }}>Retirer</button>
                    </div>
                  ))}
                  <button type="button" disabled={!netOk || submitting} onClick={handleValiderVente} style={{ marginTop: 10, padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "white", background: netOk ? "#059669" : "#94a3b8", border: "none", borderRadius: 8, cursor: netOk && !submitting ? "pointer" : "not-allowed" }}>{submitting ? "…" : "Valider"}</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {txType === "sortie" && mode === "tresorerie_directe" && (
        <div>
          {regleMatch?.compte_charge_numero?.trim() ? (
            <div
              style={{
                padding: "10px 14px",
                background: "#fef3c7",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Sparkles size={16} color="#d97706" aria-hidden />
              <span>
                Règle appliquée : <strong>{regleMatch.nom?.trim() || "Règle automatique"}</strong>
                {" → "}
                Compte <strong>{regleMatch.compte_charge_numero.trim()}</strong>
              </span>
            </div>
          ) : null}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 4 }}>Type</label>
            <select value={tresorerieForm.type} onChange={(e) => setTresorerieForm((p) => ({ ...p, type: e.target.value as TypeTresorerieDirecte }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              {(Object.keys(TRESORERIE_PRESETS_SORTIE) as (keyof typeof TRESORERIE_PRESETS_SORTIE)[]).map((t) => (
                <option key={t} value={t}>{TRESORERIE_PRESETS_SORTIE[t].label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 4 }}>Compte</label>
            <input value={tresorerieForm.compte || (tresoreriePresetFor("sortie", tresorerieForm.type) ?? TRESORERIE_PRESETS_SORTIE.autre).compte} onChange={(e) => setTresorerieForm((p) => ({ ...p, compte: e.target.value }))} placeholder={(tresoreriePresetFor("sortie", tresorerieForm.type) ?? TRESORERIE_PRESETS_SORTIE.autre).compte} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", display: "block", marginBottom: 4 }}>Libellé</label>
            <input value={tresorerieForm.libelle || (tresoreriePresetFor("sortie", tresorerieForm.type) ?? TRESORERIE_PRESETS_SORTIE.autre).libelle} onChange={(e) => setTresorerieForm((p) => ({ ...p, libelle: e.target.value }))} placeholder={(tresoreriePresetFor("sortie", tresorerieForm.type) ?? TRESORERIE_PRESETS_SORTIE.autre).libelle} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e2e8f0" }} />
          </div>
          <button type="button" disabled={submitting} onClick={handleTresorerieDirecte} style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "white", background: "#059669", border: "none", borderRadius: 8, cursor: submitting ? "not-allowed" : "pointer" }}>Valider trésorerie directe</button>
          {propositionRegleTreso && (
            <div
              style={{
                padding: 16,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                marginTop: 16,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Créer une règle automatique ?</div>
              <p style={{ fontSize: 13, color: "#475569", marginBottom: 12, marginTop: 0 }}>
                Les prochaines transactions contenant « <strong>{propositionRegleTreso.motCle}</strong> » pourront être affectées au compte{" "}
                <strong>{propositionRegleTreso.compteNumero}</strong>
                {propositionRegleTreso.tauxTva != null && propositionRegleTreso.tauxTva > 0 ? ` (TVA ${propositionRegleTreso.tauxTva} %)` : ""}.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void handleCreerRegleTreso()}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: "#2563eb",
                    color: "white",
                    fontWeight: 600,
                    cursor: submitting ? "not-allowed" : "pointer",
                    fontSize: 13,
                  }}
                >
                  Créer la règle
                </button>
                <button
                  type="button"
                  onClick={() => setPropositionRegleTreso(null)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "white",
                    color: "#475569",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Non merci
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {txType === "entree" && (
        <>
          {linkedVente.length > 0 && (
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "#059669", marginBottom: 8 }}>Facture(s) / avoir(s) rapproché(s)</div>
              {linkedVente.map((r) => (
                <div key={r.facture.id} style={{ padding: 12, background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><div style={{ fontWeight: 600 }}>{r.isAvoir ? "Avoir" : "Facture"} {r.facture.numero}</div><div style={{ fontSize: 12 }}>{fmt(r.montant)}</div></div>
                  <button type="button" onClick={() => handleDetacherVente(r.facture.id)} style={{ padding: "6px 12px", fontSize: 12, color: "#64748b", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer" }}>Détacher</button>
                </div>
              ))}
            </div>
          )}
          {montantRestantEncaissement > 0 && (
            <>
              <button type="button" onClick={handleRechercherVente} disabled={loadingVente} style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#059669", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8, cursor: loadingVente ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                {loadingVente ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Rechercher factures / avoirs
              </button>
              {searchFactureVente?.map((f) => (
                <button key={f.id} type="button" onClick={() => handleAjouterVente(f)} style={{ padding: 10, textAlign: "left", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", width: "100%" }}>
                  {f.isAvoir ? "Avoir" : "Facture"} {f.numero} · restant {fmt(Math.abs(f.restant_du_ttc))}
                </button>
              ))}
              {selectionVente.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>Sélection · Net {fmt(sommeSelection)}</div>
                  {selectionVente.map((s) => (
                    <div key={s.facture.id} style={{ padding: 10, background: "white", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                      <span>{s.facture.isAvoir ? "Avoir" : "Facture"} {s.facture.numero} · {fmt(s.montant)}</span>
                      <button type="button" onClick={() => setSelectionVente((p) => p.filter((x) => x.facture.id !== s.facture.id))} style={{ fontSize: 12, color: "#64748b" }}>Retirer</button>
                    </div>
                  ))}
                  <button type="button" disabled={!netOk || submitting} onClick={handleValiderVente} style={{ marginTop: 10, padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "white", background: netOk ? "#059669" : "#94a3b8", border: "none", borderRadius: 8, cursor: netOk && !submitting ? "pointer" : "not-allowed" }}>{submitting ? "…" : "Valider"}</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
